import { PolygonIcon, RectBboxIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import {
  Crosshair,
  FolderPlus,
  Hand,
  Info,
  Loader2,
  Maximize2,
  MessageSquare,
  MousePointer2,
  Redo2,
  Settings,
  Sparkles,
  Trash2,
  Undo2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShortcutsDialog } from "../ShortcutsDialog";
import { ToolMode } from "../../types/annotations";

export interface ToolbarProps {
  toolMode: ToolMode;
  onSetToolMode: (mode: ToolMode) => void;
  onUndo: () => void;
  onRedo: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onClear: () => void;
  canUndo: boolean;
  canRedo: boolean;
  hasSelection: boolean;
  hasLabels: boolean;
  showCrosshair: boolean;
  onToggleCrosshair: () => void;
  onResetView: () => void;
  /**
   * Run the project's default mode. Confirming that staged regions or a Vision
   * conversation will be replaced is the page's job, not the toolbar's.
   */
  onPreAnnotate: () => void;
  onOpenPreAnnotateSettings: () => void;
  preAnnotateDisabled: boolean;
  /** Why ✨ is greyed out, for its tooltip. */
  preAnnotateDisabledReason?: string;
  preAnnotatePending: boolean;
  /** Resume this image's Robopipe Vision conversation. */
  onPreAnnotateFeedback: () => void;
  /** Why Feedback is greyed — no conversation yet, or a run is in flight. */
  preAnnotateFeedbackDisabledReason?: string;
  canGroup?: boolean;
  onGroupSelected?: () => void;
}

const toolButtonClass =
  "flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted-foreground hover:bg-black/[0.06] hover:text-black [&_svg]:size-5";

export const Toolbar = ({
  toolMode,
  onSetToolMode,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onClear,
  canUndo,
  canRedo,
  hasSelection,
  hasLabels,
  showCrosshair,
  onToggleCrosshair,
  onResetView,
  onPreAnnotate,
  onOpenPreAnnotateSettings,
  preAnnotateDisabled,
  preAnnotateDisabledReason,
  preAnnotatePending,
  onPreAnnotateFeedback,
  preAnnotateFeedbackDisabledReason,
  canGroup = false,
  onGroupSelected,
}: ToolbarProps) => {
  const { t } = useTranslation(["label", "common"]);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const modeTools: {
    icon: React.ReactNode;
    title: string;
    mode: ToolMode;
    disabled?: boolean;
  }[] = [
    { icon: <MousePointer2 />, title: t("toolbar.select"), mode: ToolMode.SELECT },
    {
      icon: <RectBboxIcon />,
      title: t("toolbar.drawBbox"),
      mode: ToolMode.DRAW_BBOX,
      disabled: !hasLabels,
    },
    {
      icon: <PolygonIcon />,
      title: t("toolbar.drawPolygon"),
      mode: ToolMode.DRAW_POLYGON,
      disabled: !hasLabels,
    },
  ];

  const actionTools: {
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    disabled?: boolean;
    mode?: ToolMode;
    active?: boolean;
  }[] = [
    { icon: <Undo2 />, title: t("toolbar.undo"), onClick: onUndo, disabled: !canUndo },
    { icon: <Redo2 />, title: t("toolbar.redo"), onClick: onRedo, disabled: !canRedo },
    {
      icon: <FolderPlus />,
      title: canGroup ? t("toolbar.group") : t("toolbar.groupHint"),
      onClick: () => onGroupSelected?.(),
      disabled: !canGroup,
    },
    { icon: <ZoomIn />, title: t("toolbar.zoomIn"), onClick: onZoomIn },
    { icon: <ZoomOut />, title: t("toolbar.zoomOut"), onClick: onZoomOut },
    { icon: <Maximize2 />, title: t("toolbar.fit"), onClick: onResetView },
    {
      icon: <Hand />,
      title: t("toolbar.pan"),
      onClick: () => onSetToolMode(ToolMode.PAN),
      mode: ToolMode.PAN,
    },
    {
      icon: <Crosshair />,
      title: showCrosshair ? t("toolbar.hideCrosshair") : t("toolbar.showCrosshair"),
      onClick: onToggleCrosshair,
      active: showCrosshair,
    },
  ];

  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-black/10 bg-white/95 p-2 shadow-[0_0_12px_rgba(0,0,0,0.08)] backdrop-blur-md">
      {modeTools.map((tool) => (
        <button
          key={tool.title}
          type="button"
          className={cn(
            toolButtonClass,
            toolMode === tool.mode &&
              "bg-emerald-500/10 text-black hover:bg-emerald-500/[0.18] hover:text-black",
            tool.disabled && "cursor-not-allowed opacity-[0.35]",
          )}
          title={tool.title}
          onClick={() => onSetToolMode(tool.mode)}
          disabled={tool.disabled}
        >
          {tool.icon}
        </button>
      ))}
      <button
        type="button"
        className={cn(
          toolButtonClass,
          !hasSelection && "cursor-not-allowed opacity-[0.35]"
        )}
        title={t("toolbar.deleteSelected")}
        onClick={onClear}
        disabled={!hasSelection}
      >
        <Trash2 />
      </button>
      <div className="my-1 h-px bg-black/10" />
      {actionTools.map((tool) => (
        <button
          key={tool.title}
          type="button"
          className={cn(
            toolButtonClass,
            ((tool.mode && toolMode === tool.mode) || tool.active) &&
              "bg-emerald-500/10 text-black hover:bg-emerald-500/[0.18] hover:text-black",
            tool.disabled && "cursor-not-allowed opacity-[0.35]"
          )}
          title={tool.title}
          onClick={tool.onClick}
          disabled={tool.disabled}
        >
          {tool.icon}
        </button>
      ))}
      <div className="my-1 h-px bg-black/10" />
      <button
        type="button"
        className={cn(
          toolButtonClass,
          (preAnnotateDisabled || preAnnotatePending) &&
            "cursor-not-allowed opacity-[0.35]",
        )}
        title={preAnnotateDisabledReason ?? t("toolbar.autoAnnotateVision")}
        onClick={onPreAnnotate}
        disabled={preAnnotateDisabled || preAnnotatePending}
      >
        {preAnnotatePending ? <Loader2 className="animate-spin" /> : <Sparkles />}
      </button>
      <button
        type="button"
        className={cn(
          toolButtonClass,
          preAnnotateFeedbackDisabledReason !== undefined &&
            "cursor-not-allowed opacity-[0.35]",
        )}
        title={preAnnotateFeedbackDisabledReason ?? t("toolbar.feedback")}
        onClick={onPreAnnotateFeedback}
        disabled={preAnnotateFeedbackDisabledReason !== undefined}
      >
        <MessageSquare />
      </button>
      <button
        type="button"
        className={toolButtonClass}
        title={t("actions.autoAnnotateSettings")}
        onClick={onOpenPreAnnotateSettings}
      >
        <Settings />
      </button>
      <button
        type="button"
        className={toolButtonClass}
        title={t("shortcuts.title")}
        onClick={() => setShortcutsOpen(true)}
      >
        <Info />
      </button>
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
};
